// AppSidebar — Wave 4 grouped layout per docs/design/sidebar-organization.md
//
// Structure (Section 4.1 of the spec):
//   [Cmd-K Search Bar]
//   Dashboard, Actions, Orders, Customers   (4 daily anchors, no group)
//   ─────────
//   Suppliers                                (secondary-daily, own divider)
//   ─────────
//   PRODUCTION (Samples, QA, Shipments, Developments)
//   ─────────
//   LIBRARY    (Quotes, Costings, Stock Offers)
//   ─────────
//   Meetings                                 (trailing single)
//
//   Bottom-pinned: [+ New], Sign out
//
// On screens < 768px (md breakpoint) the sidebar collapses to an icon rail.
// The Cmd-K bar collapses to an icon-only button at that size.

import {
  LayoutDashboard,
  ListTodo,
  Package as PackageIcon,
  Users,
  Factory,
  Boxes,
  ShieldCheck,
  Truck,
  PackageCheck,
  Lightbulb,
  Receipt,
  Calculator,
  Tag,
  FileText,
  Users2,
  PlusCircle,
  LogOut,
  Search,
  Shield,
  Library,
  Package,
  Camera,
  BarChart3,
  Boxes as BoxesIcon,
  Upload,
  Download,
  FileSignature,
  CalendarRange,
  Trophy,
  Scale,
  UserCog,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Settings as SettingsIcon,
  GitBranch,
  Bell,
  History,
  MonitorPlay,
  Kanban,
  FileSpreadsheet,
  Sparkles,
  ClipboardList,
  Mail,
  Ruler,
  Building2,
  Tags,
} from "lucide-react";
import { useState } from "react";
import { useKBar } from "@refinedev/kbar";
import { NavLink, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { CountBadge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useUserRoles } from "@/hooks/useUserRoles";

interface NavItem {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  /**
   * If true, only admins should see this item. Until role-detection (e.g.
   * `staff.has_role`) is wired up, these stay visible and the page itself
   * relies on RLS for enforcement.
   */
  adminOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// 5 daily anchors — un-grouped, top of sidebar
const ANCHORS: NavItem[] = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Actions",   url: "/actions",   icon: ListTodo },
  { title: "Emails",    url: "/emails",    icon: Mail },
  { title: "Orders",    url: "/orders",    icon: PackageIcon },
  { title: "Customers", url: "/customers", icon: Users },
];

// Secondary-daily, own divider
const SUPPLIERS: NavItem = {
  title: "Suppliers",
  url: "/suppliers",
  icon: Factory,
};

// Indented "Scorecard" sub-item shown directly under Suppliers.
const SUPPLIER_SCORECARD: NavItem = {
  title: "Scorecard",
  url: "/suppliers/scorecard",
  icon: Trophy,
};

// Grouped sections
const GROUPS: NavGroup[] = [
  // W7.4: Truworths-only workflow link removed — Critical Path is universal,
  // accessed from /admin/order-workflow (admin) or per-customer profile.
  {
    label: "Production",
    items: [
      { title: "Dashboard",       url: "/dashboard/production", icon: BarChart3 },
      // Cross-stage pipeline board (#1008, Epic #956 phase 3a) — briefs and
      // orders mapped onto one stage-column view, not owned by any single
      // list page below.
      { title: "Pipeline",        url: "/pipeline",     icon: Kanban },
      { title: "Samples",         url: "/samples",      icon: Boxes },
      { title: "QA",              url: "/qa",           icon: ShieldCheck },
      { title: "QA pending",      url: "/qa/pending",   icon: ShieldCheck },
      { title: "QA dashboard",    url: "/qa/dashboard", icon: BarChart3 },
      { title: "Shipments",       url: "/shipments",    icon: Truck },
      { title: "Pending delivery", url: "/shipments/pending-delivery", icon: PackageCheck },
      { title: "Developments",    url: "/developments", icon: Lightbulb },
      { title: "Seasons & SKUs",  url: "/seasons",      icon: CalendarRange },
      // #1065 (Epic #1063 phase 1b): the Supplier POs list replaces the old
      // "Generate PO" form link. /forms/new-po no longer renders a throwaway
      // document from live order lines — it redirects here.
      { title: "Supplier POs",    url: "/supplier-pos", icon: FileSignature },
    ],
  },
  {
    // Management Overview + Reports — issue #477 (epic #460 gap g16). The
    // cross-functional roll-up for Jon/Marchant/Hamza plus the meeting-export
    // generator's own entry point, separate from the Production dashboard
    // (which stays T-Minus/ops-focused).
    label: "Reports",
    items: [
      { title: "Management overview", url: "/dashboard/management", icon: BarChart3 },
      { title: "Reports",             url: "/reports",              icon: FileSpreadsheet },
    ],
  },
  {
    // Sample Library section — Phase 7 sample-library portal pages.
    // Sits between Production and the order-sourcing "Library" so the two
    // never get confused. Admin-only items use `adminOnly: true` and are
    // hidden when role-detection is wired up; until then they show for
    // everyone and rely on RLS at the page/server level.
    label: "Library",
    items: [
      { title: "Library",      url: "/library",              icon: Library },
      { title: "AI images",    url: "/library/ai-images",    icon: Sparkles },
      { title: "Loans",          url: "/library/loans",          icon: Package },
      { title: "Presentations",         url: "/library/presentations",         icon: MonitorPlay },
      { title: "Presentation history",  url: "/library/presentations/history", icon: History },
      { title: "Meetings",      url: "/meetings",               icon: Users2 },
      { title: "Briefs",        url: "/briefs",                 icon: ClipboardList },
      // #761 — visible to anyone who can reach /briefs; the page itself
      // gates USE on the crm.map_aliases capability (ruling D30), same
      // pattern as adminOnly items below (not yet role-filtered here).
      { title: "Map unmatched names", url: "/briefs/unmatched-names", icon: ClipboardList },
      // #1059 (Epic #957 phase 3c) — the "AI documents awaiting confirmation"
      // queue. Visible to anyone who can reach /briefs (prefix match), and it
      // carries its own badge: ops.badge_source_document_extractions keys its
      // count under this exact url, so countFor() picks it up with no mapping.
      { title: "AI docs to confirm", url: "/briefs/ai-confirmations", icon: Sparkles },
      { title: "Print labels", url: "/library/labels",       icon: Tag },
      { title: "Scan",         url: "/library/scan",         icon: Camera },
      { title: "Dashboard",    url: "/library/dashboard",    icon: BarChart3 },
      { title: "Tags",         url: "/library/tags",         icon: Tag,        adminOnly: true },
      { title: "Locations",    url: "/library/locations",    icon: BoxesIcon,  adminOnly: true },
      { title: "Import",       url: "/library/import",       icon: Upload },
      { title: "Export",       url: "/library/export",       icon: Download },
      { title: "Library admin", url: "/library/admin",       icon: SettingsIcon, adminOnly: true },
    ],
  },
  {
    label: "Sourcing",
    items: [
      { title: "Quotes",       url: "/quotes",       icon: Receipt },
      { title: "Costings",     url: "/costings",     icon: Calculator },
      { title: "Stock Offers", url: "/stock-offers", icon: Tag },
    ],
  },
  {
    // Finance settings — admin-only governance surfaces. Like the Library
    // admin items above, adminOnly is informational until role-based hiding
    // is wired up; the page itself enforces admin via useIsAdmin() and RLS
    // gates writes server-side.
    label: "Finance",
    items: [
      { title: "Factor rules", url: "/finance/factor-rules", icon: Scale, adminOnly: true },
    ],
  },
];

// Settings — bottom section, above the [+ New] / Sign-out block
const SECURITY: NavItem = {
  title: "Security",
  url: "/settings/security",
  icon: Shield,
};

// Admin group — only rendered when useIsAdmin() returns true. Sits at the
// bottom of the nav list, beneath Security but above the [+ New] / Sign-out
// block. W13 expanded this with all the admin CRUD surfaces (seasons, plus
// aliases for the existing /library/* admin pages so they're discoverable
// from one place, plus placeholders for W7.4 / W14 surfaces).
const ADMIN_GROUP: NavGroup = {
  label: "Admin",
  items: [
    { title: "Users",             url: "/admin/users",          icon: UserCog,    adminOnly: true },
    { title: "Seasons",           url: "/admin/seasons",        icon: CalendarRange, adminOnly: true },
    { title: "Tags",              url: "/admin/tags",           icon: Tag,        adminOnly: true },
    { title: "Doc types",         url: "/admin/doc-types",      icon: FileText,   adminOnly: true },
    { title: "Storage Locations", url: "/admin/locations",      icon: BoxesIcon,  adminOnly: true },
    { title: "Order Workflow",    url: "/admin/order-workflow", icon: GitBranch,  adminOnly: true },
    { title: "Sizes",             url: "/admin/sizes",          icon: Ruler,      adminOnly: true },
    { title: "Brands",            url: "/admin/brands",         icon: Building2,  adminOnly: true },
    // #795 — kept as its own item right next to Brands (not folded into a
    // tab on /admin/brands) since production.labels has no customer scope or
    // parent/child nesting to master-detail over. "Brand labels", never bare
    // "Labels" (#798) — "Labels" already means the Sample Library's print/QR
    // labels at /library/labels.
    { title: "Brand labels",      url: "/admin/labels",         icon: Tags,       adminOnly: true },
    // #1074 (Epic #1072 phase 1b) — external parties: receivers, inspectors,
    // importers of record, clearing agents, forwarders.
    { title: "Parties",           url: "/admin/parties",        icon: Users,      adminOnly: true },
    // W14 (Item #8) — Notifications + Settings live next to one another so
    // admins can jump between system state and config in one click.
    { title: "Notifications",     url: "/admin/notifications",  icon: Bell,         adminOnly: true },
    { title: "Settings",          url: "/admin/settings",       icon: SettingsIcon, adminOnly: true },
    { title: "Export templates",  url: "/admin/export-templates", icon: FileSpreadsheet, adminOnly: true },
    { title: "AI usage",          url: "/admin/ai-usage",         icon: Sparkles,        adminOnly: true },
  ],
};

// Help & Docs — pinned beneath the Admin group so every authenticated user
// has a one-click path to the user manual, workflows, and glossary. Visible
// to everyone (no role gate). Highlights when on any /docs/* route.
const HELP_DOCS: NavItem = {
  title: "Help & Docs",
  url: "/docs",
  icon: BookOpen,
};

function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    "md:justify-start justify-center",
    isActive && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
  );
}

// `count` is this row's exact badge count from useBadgeCounts().countFor(url)
// — already gated server-side (public.get_my_badge_counts(), #960), so this
// component never decides visibility itself, only whether to render a number
// it was handed. Two renderings of the same count, swapped by breakpoint:
//   - icon-rail (< md): a small overlay badge in the icon's corner.
//   - expanded sidebar (>= md): an inline pill at the end of the row.
// CountBadge itself renders nothing for count <= 0, so both are safe to
// mount unconditionally.
function NavRow({ item, count = 0 }: { item: NavItem; count?: number }) {
  return (
    <NavLink to={item.url} className={navLinkClass} title={item.title}>
      <span className="relative flex-shrink-0">
        <item.icon className="h-4 w-4" />
        <CountBadge
          count={count}
          className="md:hidden absolute -right-2 -top-2 h-4 min-w-[1rem] px-0.5 text-[9px]"
        />
      </span>
      <span className="hidden md:inline flex-1 truncate">{item.title}</span>
      <span
        className="hidden md:inline-flex"
        data-testid={`sidebar-badge-${item.url}`}
      >
        <CountBadge count={count} />
      </span>
    </NavLink>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="hidden md:block px-3 pt-3 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground/80">
      {children}
    </div>
  );
}

function CommandSearchBar() {
  const { query } = useKBar();
  return (
    <button
      type="button"
      onClick={() => query.toggle()}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-sidebar-border",
        "bg-sidebar-accent/40 px-3 py-2 text-sm text-muted-foreground",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
        "md:justify-between justify-center",
      )}
      title="Search and quick actions (Cmd K)"
    >
      <span className="flex items-center gap-2">
        <Search className="h-4 w-4 flex-shrink-0" />
        <span className="hidden md:inline">Search…</span>
      </span>
      <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        <span className="text-base leading-none">⌘</span>K
      </kbd>
    </button>
  );
}

// `countFor` is threaded in from `Layout` (the single `useBadgeCounts()`
// owner — see that component) rather than called again here. AppSidebar is
// always rendered as Layout's direct child (it has no other mount point —
// see useBadgeCounts.ts's own header), so a second independent hook call in
// this component bought nothing but a second `get_my_badge_counts()` RPC
// round trip and a second realtime-channel subscribe attempt on literally
// every authenticated page load — doubling the load this feature adds to
// every route for no behavioural benefit (#1019). One owner, one fetch, one
// subscription per page.
export function AppSidebar({ countFor }: { countFor: (url: string) => number }) {
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  // Additive RBAC: each nav item is rendered only when at least one of the
  // user's roles grants access to its URL prefix. `isAllowed(path)` does the
  // union check across every role the user holds — adding more roles
  // strictly adds nav entries, never removes them. See hooks/useUserRoles.ts
  // for the role-to-surface map.
  const { isAllowed, loading: rolesLoading } = useUserRoles();
  // Admin group expands by default for admins so the new surfaces are
  // discoverable. Collapse persists for the session via useState only.
  const [adminOpen, setAdminOpen] = useState(true);

  // While roles are loading, show every nav item — better than flashing an
  // empty sidebar at any operator. Once roles resolve, the filter kicks in.
  const showItem = (url: string) => rolesLoading || isAllowed(url);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  return (
    <aside
      className={cn(
        "flex flex-shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground",
        "w-16 md:w-60",
      )}
    >
      <div className="flex h-14 items-center border-b border-sidebar-border px-3 md:px-4">
        <NavLink
          to="/dashboard"
          className="flex items-center gap-2 font-semibold w-full md:w-auto justify-center md:justify-start"
        >
          <LayoutDashboard className="h-5 w-5 text-primary" />
          <span className="hidden md:inline">cc-os</span>
        </NavLink>
      </div>

      <div className="px-3 pt-3 pb-1">
        <CommandSearchBar />
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 p-3 overflow-y-auto">
        {/* 4 daily anchors — each shown only when at least one of the user's
            roles grants access to that URL. Additive: a sample-coordinator
            with no broadening role sees nothing here; add `merch` or
            `kam` and the relevant anchors appear. */}
        {ANCHORS.filter((item) => showItem(item.url)).map((item) => (
          <NavRow key={item.url} item={item} count={countFor(item.url)} />
        ))}

        {ANCHORS.some((item) => showItem(item.url)) && <Separator className="my-2" />}

        {/* Suppliers + Scorecard sub-item — gated on /suppliers surface */}
        {showItem(SUPPLIERS.url) && (
          <>
            <NavRow item={SUPPLIERS} count={countFor(SUPPLIERS.url)} />
            <div className="md:pl-4">
              <NavRow item={SUPPLIER_SCORECARD} count={countFor(SUPPLIER_SCORECARD.url)} />
            </div>
          </>
        )}

        {/* Production + Library + Sourcing + Finance groups. Each group's
            items are filtered individually so a user with partial coverage
            (e.g. `qa` who can hit /qa but not /samples) still sees the
            Production label with only the QA rows under it. Empty groups
            are dropped entirely so the sidebar stays scannable. */}
        {GROUPS.map((group) => {
          const visibleItems = group.items.filter((item) => showItem(item.url));
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.label} className="flex flex-col gap-0.5">
              <Separator className="my-2" />
              <GroupLabel>{group.label}</GroupLabel>
              {visibleItems.map((item) => (
                <NavRow key={item.url} item={item} count={countFor(item.url)} />
              ))}
            </div>
          );
        })}

        <Separator className="my-2" />

        {/* Security (account settings) — every signed-in user keeps this */}
        <NavRow item={SECURITY} count={countFor(SECURITY.url)} />

        {/* Admin group — only visible to portal admins. Hidden entirely for
            non-admins so the sidebar stays scannable. The pages themselves
            also enforce admin via useIsAdmin() and RLS gates the verbs.
            W13: collapsible (chevron toggles adminOpen). On the icon-rail
            (md:<60) the group is always expanded so all admin surfaces stay
            reachable from the rail. */}
        {isAdmin && (
          <div className="flex flex-col gap-0.5">
            <Separator className="my-2" />
            <button
              type="button"
              onClick={() => setAdminOpen((v) => !v)}
              className={cn(
                "hidden md:flex w-full items-center justify-between rounded-md px-3 pt-3 pb-1",
                "text-[11px] uppercase tracking-wide text-muted-foreground/80 hover:text-foreground transition-colors",
              )}
              aria-expanded={adminOpen}
              aria-controls="admin-group"
            >
              <span>{ADMIN_GROUP.label}</span>
              {adminOpen ? (
                <ChevronDown className="h-3 w-3" aria-hidden />
              ) : (
                <ChevronRight className="h-3 w-3" aria-hidden />
              )}
            </button>
            <div id="admin-group" className={cn("flex flex-col gap-0.5", !adminOpen && "md:hidden")}>
              {ADMIN_GROUP.items.map((item) => (
                <NavRow key={item.url} item={item} count={countFor(item.url)} />
              ))}
            </div>
          </div>
        )}

        {/* Help & Docs — pinned at the bottom of the nav. Visible to every
            authenticated user. The /docs/* prefix highlights this entry. */}
        <Separator className="my-2" />
        <NavLink
          to={HELP_DOCS.url}
          className={navLinkClass}
          title={HELP_DOCS.title}
        >
          <HELP_DOCS.icon className="h-4 w-4 flex-shrink-0" />
          <span className="hidden md:inline">{HELP_DOCS.title}</span>
        </NavLink>
      </nav>

      <Separator />
      <div className="flex flex-col gap-1 p-3">
        {/* The [+ New] forms hub covers POs, quotes, customer/supplier
            creation, etc. — none of which a sample-coordinator should be
            able to fire off. The forms pages themselves enforce role gates
            server-side too, but hiding the entry-point keeps the sidebar
            scannable for them. */}
        {showItem("/forms") && (
          <NavLink
            to="/forms"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                "border border-sidebar-border bg-sidebar-accent/30",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                "md:justify-start justify-center",
                isActive && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
              )
            }
            title="New (forms hub)"
          >
            <PlusCircle className="h-4 w-4 flex-shrink-0" />
            <span className="hidden md:inline">New</span>
          </NavLink>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center md:justify-start gap-3"
          onClick={handleLogout}
          title="Sign out"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          <span className="hidden md:inline">Sign out</span>
        </Button>
      </div>
    </aside>
  );
}
